import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDashboardStore from '../../stores/dashboardStore';

const MatchCard = () => {
    const { t, i18n } = useTranslation();
    const currentMatch = useDashboardStore(s => s.currentMatch);

    const getCurrentWeekDateRange = () => {
        const today = new Date();
        const currentMonday = new Date(today);
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        currentMonday.setDate(today.getDate() - diffToMonday);
        const currentSunday = new Date(currentMonday);
        currentSunday.setDate(currentMonday.getDate() + 6);
        const options = { month: 'short', day: 'numeric' };
        const start = currentMonday.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', options);
        const end = currentSunday.toLocaleDateString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', options);
        return `(${start} — ${end})`;
    };

    if (!currentMatch) return null;

    return (
        <div className="glass-card" style={{ padding: '2rem', marginBottom: '1.5rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)' }}>
            <h2 className="section-title" style={{ marginBottom: '0.5rem' }}>
                {t('dashboard.matching.current_match', 'Current match')}
            </h2>
            <div style={{
                textTransform: 'uppercase',
                color: '#374151',
                fontSize: '0.75rem',
                fontWeight: '600',
                marginBottom: '1.5rem',
                letterSpacing: '0.05em'
            }}>
                {t('dashboard.matching.this_week', 'This week')} <span style={{ color: '#6b7280', fontWeight: 'normal' }}>{getCurrentWeekDateRange()}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #fff',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    flexShrink: 0
                }}>
                    {currentMatch.avatar ? (
                        <img
                            src={currentMatch.avatar}
                            alt={`${currentMatch.name} ${currentMatch.family}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '2rem',
                            fontWeight: 'bold'
                        }}>
                            {currentMatch.name ? currentMatch.name.charAt(0).toUpperCase() : '?'}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <Link
                        to={`/profile/${currentMatch.username.replace('@', '')}`}
                        style={{ textDecoration: 'none' }}
                    >
                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937', cursor: 'pointer' }}>
                            {currentMatch.name} {currentMatch.family}
                        </h3>
                    </Link>
                    {currentMatch.username && (
                        <a
                            href={`https://t.me/${currentMatch.username.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#3b82f6',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 2L11 13"></path>
                                <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
                            </svg>
                            @{currentMatch.username.replace('@', '')}
                        </a>
                    )}
                    <Link
                        to={`/profile/${currentMatch.username.replace('@', '')}`}
                        style={{
                            color: '#6b7280',
                            textDecoration: 'none',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginTop: '0.25rem'
                        }}
                    >
                        {t('dashboard.matching.view_profile', 'View Profile')} &rarr;
                    </Link>
                </div>
            </div>

            {/* AI Intro / Context */}
            {currentMatch.intro && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <h4 style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        ✨ Why you matched
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#374151', lineHeight: '1.5', fontStyle: 'italic', marginBottom: '1rem' }}>
                        "{currentMatch.intro.why_interesting}"
                    </p>

                    {currentMatch.intro.conversation_starters && currentMatch.intro.conversation_starters.length > 0 && (
                        <div>
                            <h4 style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                💬 Icebreakers
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {currentMatch.intro.conversation_starters.map((starter, idx) => (
                                    <div key={idx} style={{
                                        background: 'white',
                                        padding: '0.5rem 0.75rem',
                                        borderRadius: '0.5rem',
                                        fontSize: '0.85rem',
                                        color: '#4b5563',
                                        border: '1px solid rgba(0,0,0,0.05)'
                                    }}>
                                        {starter}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MatchCard;
